/**
 * BPM Range Filter Component
 * Dual slider for filtering tracks by BPM range
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as SliderPrimitive from "@radix-ui/react-slider";

const BPMRangeFilter = ({ 
  minBPM = 60, 
  maxBPM = 200, 
  value = [60, 200], 
  onChange,
  onReset,
  isActive = false
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleValueChange = (newValue) => {
    setLocalValue(newValue);
  };

  const handleValueCommit = (newValue) => {
    onChange?.(newValue);
  };

  const handleReset = () => {
    setLocalValue([minBPM, maxBPM]);
    onChange?.([minBPM, maxBPM]);
    onReset?.();
  };

  const isFiltered = localValue[0] !== minBPM || localValue[1] !== maxBPM;

  return (
    <div className="relative">
      {/* Toggle Button */}
      <motion.button
        onClick={() => setIsExpanded(!isExpanded)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
          isFiltered 
            ? 'bg-[#00D9FF]/20 text-[#00D9FF] border border-[#00D9FF]/50' 
            : 'bg-[#141414] text-[#888] border border-[#262626] hover:border-[#333]'
        }`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/>
        </svg>
        <span>BPM</span>
        {isFiltered && (
          <motion.span 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#00D9FF] text-black px-1.5 py-0.5 rounded text-[10px] font-bold"
          >
            {localValue[0]}-{localValue[1]}
          </motion.span>
        )}
        <motion.svg 
          animate={{ rotate: isExpanded ? 180 : 0 }}
          className="w-3 h-3 ml-1"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
        </motion.svg>
      </motion.button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-2 w-72 bg-[#141414] border border-[#262626] rounded-xl p-4 shadow-xl z-50"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-white">Filtrar por BPM</span>
              {isFiltered && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onClick={handleReset}
                  className="text-xs text-[#00D9FF] hover:underline"
                >
                  Resetear
                </motion.button>
              )}
            </div>

            {/* BPM Display */}
            <div className="flex items-center justify-between mb-4">
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 w-20 text-center">
                <span className="text-lg font-bold text-[#00D9FF]">{localValue[0]}</span>
                <span className="text-[10px] text-[#555] block">MIN</span>
              </div>
              <div className="flex-1 mx-3 h-px bg-[#262626]"></div>
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg px-3 py-2 w-20 text-center">
                <span className="text-lg font-bold text-[#00D9FF]">{localValue[1]}</span>
                <span className="text-[10px] text-[#555] block">MAX</span>
              </div>
            </div>

            {/* Dual Range Slider */}
            <div className="px-2 py-4">
              <SliderPrimitive.Root
                value={localValue}
                onValueChange={handleValueChange}
                onValueCommit={handleValueCommit}
                min={minBPM}
                max={maxBPM}
                step={1}
                minStepsBetweenThumbs={5}
                className="relative flex w-full touch-none select-none items-center"
              >
                <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-[#262626]">
                  <SliderPrimitive.Range className="absolute h-full bg-gradient-to-r from-[#00D9FF] to-[#00D9FF]" />
                </SliderPrimitive.Track>
                <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-[#00D9FF] bg-[#0A0A0A] shadow-lg shadow-[#00D9FF]/30 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00D9FF] hover:scale-110 cursor-grab active:cursor-grabbing" />
                <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-[#00D9FF] bg-[#0A0A0A] shadow-lg shadow-[#00D9FF]/30 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00D9FF] hover:scale-110 cursor-grab active:cursor-grabbing" />
              </SliderPrimitive.Root>
            </div>

            {/* Quick Presets */}
            <div className="mt-3 pt-3 border-t border-[#262626]">
              <span className="text-[10px] text-[#555] uppercase tracking-wide block mb-2">Presets rápidos</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Hip Hop', range: [80, 100] },
                  { label: 'House', range: [118, 130] },
                  { label: 'Reggaetón', range: [85, 100] },
                  { label: 'Dembow', range: [110, 125] },
                  { label: 'Techno', range: [130, 150] },
                ].map((preset) => (
                  <motion.button
                    key={preset.label}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setLocalValue(preset.range);
                      onChange?.(preset.range);
                    }}
                    className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                      localValue[0] === preset.range[0] && localValue[1] === preset.range[1]
                        ? 'bg-[#00D9FF]/20 text-[#00D9FF] border border-[#00D9FF]/50'
                        : 'bg-[#0A0A0A] text-[#888] border border-[#262626] hover:text-white hover:border-[#333]'
                    }`}
                  >
                    {preset.label}
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close */}
      {isExpanded && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
};

export default BPMRangeFilter;
